/**
 * MeasurementTool Component - WESTEER Design System
 *
 * @version 1.1
 * @updated 2026-05-04
 * @module core/map-tools/measurement
 *
 * @description
 * Herramienta para medir distancias y áreas en el mapa.
 * Se integra con la calculadora para enviar valores medidos.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Feature, FeatureCollection, LineString, Point, Polygon } from 'geojson';
import type { Map as MapLibreMap, LngLat, MapMouseEvent } from 'maplibre-gl';
import { Ruler, X, Calculator, Trash2, Copy, Check } from 'lucide-react';
import * as turf from '@turf/turf';
import './measurement.css';

interface MeasurementToolProps {
  map: MapLibreMap | null;
  isActive: boolean;
  onDeactivate: () => void;
  onSendToCalculator?: (value: number) => void;
}

interface MeasurementPoint {
  id: string;
  lngLat: LngLat;
}

const CLOSE_THRESHOLD_PX = 14;
const STYLE_REINJECTION_DELAY_MS = 150;

const IMAGE_ID = 'measurement-x-marker';
const SOURCE_LINE_ID = 'measurement-line';
const SOURCE_HOVER_LINE_ID = 'measurement-hover-line';
const SOURCE_POLYGON_ID = 'measurement-polygon';
const SOURCE_POINTS_ID = 'measurement-points';
const SOURCE_LABELS_ID = 'measurement-labels';

const LAYER_LINE_SHADOW_ID = 'measurement-line-shadow';
const LAYER_LINE_ID = 'measurement-line-layer';
const LAYER_HOVER_SHADOW_ID = 'measurement-hover-shadow';
const LAYER_HOVER_LINE_ID = 'measurement-hover-line-layer';
const LAYER_POLYGON_ID = 'measurement-polygon-layer';
const LAYER_POINTS_ID = 'measurement-points-layer';
const LAYER_LABELS_ID = 'measurement-labels-layer';

const EMPTY_LINE_FEATURE: Feature<LineString> = {
  type: 'Feature',
  properties: {},
  geometry: {
    type: 'LineString',
    coordinates: [],
  },
};

const EMPTY_POLYGON_FEATURE: Feature<Polygon> = {
  type: 'Feature',
  properties: {},
  geometry: {
    type: 'Polygon',
    coordinates: [[]],
  },
};

const EMPTY_FEATURE_COLLECTION: FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

declare global {
  interface Window {
    __WESTEER_MEASUREMENT_DEBUG__?: boolean;
  }
}

function isEditableElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

function formatNumericValue(value: number, maxFractionDigits = 2): string {
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: value < 10 ? 2 : 0,
    maximumFractionDigits: value < 10 ? maxFractionDigits : Math.min(maxFractionDigits, 1),
  }).format(value);
}

function formatDistanceValue(distanceMeters: number): string {
  if (distanceMeters >= 1000) {
    return `${formatNumericValue(distanceMeters / 1000)} km`;
  }

  return `${formatNumericValue(distanceMeters)} m`;
}

function formatAreaValue(areaSquareMeters: number): string {
  if (areaSquareMeters >= 1_000_000) {
    return `${formatNumericValue(areaSquareMeters / 1_000_000)} km²`;
  }

  if (areaSquareMeters >= 10_000) {
    return `${formatNumericValue(areaSquareMeters / 10_000)} ha`;
  }

  return `${formatNumericValue(areaSquareMeters)} m²`;
}

function isMeasurementDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  return window.__WESTEER_MEASUREMENT_DEBUG__ === true;
}

function debugMeasurementTiming(label: string, startTime: number): void {
  if (!isMeasurementDebugEnabled()) return;

  const elapsed = performance.now() - startTime;
  console.debug(`[MeasurementTool] ${label}: ${elapsed.toFixed(2)}ms`);
}

function debugMeasurementEvent(label: string, payload?: Record<string, unknown>): void {
  if (!isMeasurementDebugEnabled()) return;

  if (payload) {
    console.debug(`[MeasurementTool] ${label}`, payload);
    return;
  }

  console.debug(`[MeasurementTool] ${label}`);
}

function removeMeasurementArtifacts(map: MapLibreMap): void {
  if (map.getLayer(LAYER_LINE_SHADOW_ID)) {
    map.removeLayer(LAYER_LINE_SHADOW_ID);
  }
  if (map.getLayer(LAYER_LINE_ID)) {
    map.removeLayer(LAYER_LINE_ID);
  }
  if (map.getLayer(LAYER_HOVER_SHADOW_ID)) {
    map.removeLayer(LAYER_HOVER_SHADOW_ID);
  }
  if (map.getLayer(LAYER_HOVER_LINE_ID)) {
    map.removeLayer(LAYER_HOVER_LINE_ID);
  }
  if (map.getLayer(LAYER_POLYGON_ID)) {
    map.removeLayer(LAYER_POLYGON_ID);
  }
  if (map.getLayer(LAYER_POINTS_ID)) {
    map.removeLayer(LAYER_POINTS_ID);
  }
  if (map.getLayer(LAYER_LABELS_ID)) {
    map.removeLayer(LAYER_LABELS_ID);
  }

  if (map.getSource(SOURCE_LINE_ID)) {
    map.removeSource(SOURCE_LINE_ID);
  }
  if (map.getSource(SOURCE_HOVER_LINE_ID)) {
    map.removeSource(SOURCE_HOVER_LINE_ID);
  }
  if (map.getSource(SOURCE_POLYGON_ID)) {
    map.removeSource(SOURCE_POLYGON_ID);
  }
  if (map.getSource(SOURCE_POINTS_ID)) {
    map.removeSource(SOURCE_POINTS_ID);
  }
  if (map.getSource(SOURCE_LABELS_ID)) {
    map.removeSource(SOURCE_LABELS_ID);
  }
}

export function MeasurementTool({
  map,
  isActive,
  onDeactivate,
  onSendToCalculator,
}: MeasurementToolProps): JSX.Element | null {
  const [points, setPoints] = useState<MeasurementPoint[]>([]);
  const [distance, setDistance] = useState<number>(0);
  const [area, setArea] = useState<number>(0);
  const [isClosed, setIsClosed] = useState<boolean>(false);
  const [isHoveringFirstPoint, setIsHoveringFirstPoint] = useState<boolean>(false);
  const [copiedField, setCopiedField] = useState<'distance' | 'area' | null>(null);
  const [, setStyleRefreshNonce] = useState<number>(0);

  const markerImageCreated = useRef<boolean>(false);
  const infrastructureReadyRef = useRef<boolean>(false);
  const pointsRef = useRef<MeasurementPoint[]>([]);
  const isClosedRef = useRef<boolean>(false);
  const isHoveringFirstPointRef = useRef<boolean>(false);
  const hoverPositionRef = useRef<LngLat | null>(null);
  const styleReloadTimerRef = useRef<number | null>(null);
  const copyFeedbackTimerRef = useRef<number | null>(null);
  const styledataCountRef = useRef<number>(0);
  const debugCountersRef = useRef({
    handleMapClick: 0,
    handleMouseMove: 0,
    syncMeasurementSources: 0,
    syncHoverPreview: 0,
    ensureMeasurementInfrastructure: 0,
    styledata: 0,
    addImage: 0,
    addSource: 0,
    addLayer: 0,
    setDataLine: 0,
    setDataHover: 0,
    setDataPolygon: 0,
    setDataPoints: 0,
    setDataLabels: 0,
  });

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  useEffect(() => {
    isClosedRef.current = isClosed;
  }, [isClosed]);

  useEffect(() => {
    isHoveringFirstPointRef.current = isHoveringFirstPoint;
  }, [isHoveringFirstPoint]);

  const clearRenderedMeasurement = useCallback((currentMap: MapLibreMap | null): void => {
    if (!currentMap) return;
    if (!currentMap.getSource(SOURCE_LINE_ID)) return;

    (currentMap.getSource(SOURCE_LINE_ID) as maplibregl.GeoJSONSource | undefined)?.setData(
      EMPTY_LINE_FEATURE
    );
    (currentMap.getSource(SOURCE_HOVER_LINE_ID) as maplibregl.GeoJSONSource | undefined)?.setData(
      EMPTY_LINE_FEATURE
    );
    (currentMap.getSource(SOURCE_POLYGON_ID) as maplibregl.GeoJSONSource | undefined)?.setData(
      EMPTY_POLYGON_FEATURE
    );
    (currentMap.getSource(SOURCE_POINTS_ID) as maplibregl.GeoJSONSource | undefined)?.setData(
      EMPTY_FEATURE_COLLECTION
    );
    (currentMap.getSource(SOURCE_LABELS_ID) as maplibregl.GeoJSONSource | undefined)?.setData(
      EMPTY_FEATURE_COLLECTION
    );
  }, []);

  const ensureMarkerImage = useCallback((currentMap: MapLibreMap): boolean => {
    const startTime = performance.now();

    if (markerImageCreated.current && currentMap.hasImage(IMAGE_ID)) {
      debugMeasurementTiming('ensureMarkerImage (cached)', startTime);
      return true;
    }

    if (!currentMap.hasImage(IMAGE_ID)) {
      debugCountersRef.current.addImage += 1;
      const size = 20;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return false;
      }

      ctx.strokeStyle = '#FFEB3B';
      ctx.lineWidth = 3;
      ctx.shadowColor = '#000000';
      ctx.shadowBlur = 4;

      ctx.beginPath();
      ctx.moveTo(4, 4);
      ctx.lineTo(size - 4, size - 4);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(size - 4, 4);
      ctx.lineTo(4, size - 4);
      ctx.stroke();

      currentMap.addImage(IMAGE_ID, {
        width: size,
        height: size,
        data: ctx.getImageData(0, 0, size, size).data as unknown as Uint8Array,
      });
    }

    markerImageCreated.current = true;
    debugMeasurementTiming('ensureMarkerImage', startTime);
    return true;
  }, []);

  const hasMeasurementInfrastructure = useCallback((currentMap: MapLibreMap): boolean => {
    return Boolean(
      currentMap.getSource(SOURCE_LINE_ID) &&
        currentMap.getSource(SOURCE_HOVER_LINE_ID) &&
        currentMap.getSource(SOURCE_POLYGON_ID) &&
        currentMap.getSource(SOURCE_POINTS_ID) &&
        currentMap.getSource(SOURCE_LABELS_ID) &&
        currentMap.getLayer(LAYER_LINE_SHADOW_ID) &&
        currentMap.getLayer(LAYER_LINE_ID) &&
        currentMap.getLayer(LAYER_HOVER_SHADOW_ID) &&
        currentMap.getLayer(LAYER_HOVER_LINE_ID) &&
        currentMap.getLayer(LAYER_POLYGON_ID) &&
        currentMap.getLayer(LAYER_POINTS_ID) &&
        currentMap.getLayer(LAYER_LABELS_ID)
    );
  }, []);

  const ensureMeasurementInfrastructure = useCallback(
    (currentMap: MapLibreMap): boolean => {
      const startTime = performance.now();
      debugCountersRef.current.ensureMeasurementInfrastructure += 1;

      if (infrastructureReadyRef.current && hasMeasurementInfrastructure(currentMap)) {
        debugMeasurementTiming('ensureMeasurementInfrastructure (cached)', startTime);
        return true;
      }

      if (!currentMap.isStyleLoaded()) {
        return false;
      }

      if (!ensureMarkerImage(currentMap)) {
        return false;
      }

      if (!currentMap.getSource(SOURCE_LINE_ID)) {
        debugCountersRef.current.addSource += 1;
        currentMap.addSource(SOURCE_LINE_ID, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [],
            },
          },
        });
      }

      if (!currentMap.getLayer(LAYER_LINE_SHADOW_ID)) {
        debugCountersRef.current.addLayer += 1;
        currentMap.addLayer({
          id: LAYER_LINE_SHADOW_ID,
          type: 'line',
          source: SOURCE_LINE_ID,
          paint: {
            'line-color': '#000000',
            'line-width': 5,
            'line-opacity': 0.5,
            'line-dasharray': [2, 2],
            'line-blur': 2,
          },
        });
      }

      if (!currentMap.getLayer(LAYER_LINE_ID)) {
        debugCountersRef.current.addLayer += 1;
        currentMap.addLayer({
          id: LAYER_LINE_ID,
          type: 'line',
          source: SOURCE_LINE_ID,
          paint: {
            'line-color': '#FFEB3B',
            'line-width': 3,
            'line-opacity': 1,
            'line-dasharray': [2, 2],
          },
        });
      }

      if (!currentMap.getSource(SOURCE_HOVER_LINE_ID)) {
        debugCountersRef.current.addSource += 1;
        currentMap.addSource(SOURCE_HOVER_LINE_ID, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [],
            },
          },
        });
      }

      if (!currentMap.getLayer(LAYER_HOVER_SHADOW_ID)) {
        debugCountersRef.current.addLayer += 1;
        currentMap.addLayer({
          id: LAYER_HOVER_SHADOW_ID,
          type: 'line',
          source: SOURCE_HOVER_LINE_ID,
          paint: {
            'line-color': '#000000',
            'line-width': 5,
            'line-opacity': 0.3,
            'line-dasharray': [4, 4],
            'line-blur': 2,
          },
        });
      }

      if (!currentMap.getLayer(LAYER_HOVER_LINE_ID)) {
        debugCountersRef.current.addLayer += 1;
        currentMap.addLayer({
          id: LAYER_HOVER_LINE_ID,
          type: 'line',
          source: SOURCE_HOVER_LINE_ID,
          paint: {
            'line-color': '#00FF00',
            'line-width': 3,
            'line-opacity': 0.8,
            'line-dasharray': [4, 4],
          },
        });
      }

      if (!currentMap.getSource(SOURCE_POLYGON_ID)) {
        debugCountersRef.current.addSource += 1;
        currentMap.addSource(SOURCE_POLYGON_ID, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [[]],
            },
          },
        });
      }

      if (!currentMap.getLayer(LAYER_POLYGON_ID)) {
        debugCountersRef.current.addLayer += 1;
        currentMap.addLayer({
          id: LAYER_POLYGON_ID,
          type: 'fill',
          source: SOURCE_POLYGON_ID,
          paint: {
            'fill-color': '#FFEB3B',
            'fill-opacity': 0.5,
          },
        });
      }

      if (!currentMap.getSource(SOURCE_POINTS_ID)) {
        debugCountersRef.current.addSource += 1;
        currentMap.addSource(SOURCE_POINTS_ID, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
      }

      if (!currentMap.getLayer(LAYER_POINTS_ID)) {
        debugCountersRef.current.addLayer += 1;
        currentMap.addLayer({
          id: LAYER_POINTS_ID,
          type: 'symbol',
          source: SOURCE_POINTS_ID,
          layout: {
            'icon-image': IMAGE_ID,
            'icon-size': 1,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
        });
      }

      if (!currentMap.getSource(SOURCE_LABELS_ID)) {
        debugCountersRef.current.addSource += 1;
        currentMap.addSource(SOURCE_LABELS_ID, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
      }

      if (!currentMap.getLayer(LAYER_LABELS_ID)) {
        debugCountersRef.current.addLayer += 1;
        currentMap.addLayer({
          id: LAYER_LABELS_ID,
          type: 'symbol',
          source: SOURCE_LABELS_ID,
          layout: {
            'text-field': ['get', 'distance'],
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 12,
            'text-offset': [0, -1],
            'text-anchor': 'center',
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#FFEB3B',
            'text-halo-color': '#000000',
            'text-halo-width': 2,
            'text-halo-blur': 1,
          },
        });
      }

      infrastructureReadyRef.current = true;
      debugMeasurementTiming('ensureMeasurementInfrastructure', startTime);
      return true;
    },
    [ensureMarkerImage, hasMeasurementInfrastructure]
  );

  const calculateDistance = useCallback((pts: MeasurementPoint[]): number => {
    if (pts.length < 2) return 0;

    const coordinates = pts.map((point) => [point.lngLat.lng, point.lngLat.lat]);
    return turf.length(turf.lineString(coordinates), { units: 'meters' });
  }, []);

  const calculateArea = useCallback((pts: MeasurementPoint[]): number => {
    if (pts.length < 3) return 0;

    const coordinates = pts.map((point) => [point.lngLat.lng, point.lngLat.lat]);
    coordinates.push(coordinates[0]);

    return turf.area(turf.polygon([coordinates]));
  }, []);

  const checkNearFirstPoint = useCallback(
    (lngLat: LngLat, pts: MeasurementPoint[]): boolean => {
      if (!map || pts.length < 3) return false;

      const firstPoint = pts[0];
      const firstProjected = map.project(firstPoint.lngLat);
      const targetProjected = map.project(lngLat);
      const dx = targetProjected.x - firstProjected.x;
      const dy = targetProjected.y - firstProjected.y;

      return Math.sqrt(dx * dx + dy * dy) <= CLOSE_THRESHOLD_PX;
    },
    [map]
  );

  const syncHoverPreview = useCallback(
    (
      currentMap: MapLibreMap,
      currentPoints: MeasurementPoint[],
      currentIsClosed: boolean,
      currentHoverPosition: LngLat | null,
      currentIsHoveringFirstPoint: boolean
    ): void => {
      const startTime = performance.now();
      debugCountersRef.current.syncHoverPreview += 1;

      if (!infrastructureReadyRef.current || !hasMeasurementInfrastructure(currentMap)) {
        debugMeasurementTiming('syncHoverPreview (skipped - infrastructure missing)', startTime);
        return;
      }

      const hoverLineSource = currentMap.getSource(SOURCE_HOVER_LINE_ID) as
        | maplibregl.GeoJSONSource
        | undefined;

      if (
        currentIsHoveringFirstPoint &&
        currentHoverPosition &&
        currentPoints.length >= 3 &&
        !currentIsClosed
      ) {
        const firstPoint = currentPoints[0];
        const lastPoint = currentPoints[currentPoints.length - 1];
        debugCountersRef.current.setDataHover += 1;
        hoverLineSource?.setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [
              [lastPoint.lngLat.lng, lastPoint.lngLat.lat],
              [firstPoint.lngLat.lng, firstPoint.lngLat.lat],
            ],
          },
        });
      } else {
        debugCountersRef.current.setDataHover += 1;
        hoverLineSource?.setData(EMPTY_LINE_FEATURE);
      }

      debugMeasurementTiming('syncHoverPreview', startTime);
    },
    [hasMeasurementInfrastructure]
  );

  const syncMeasurementSources = useCallback(
    (
      currentMap: MapLibreMap,
      currentPoints: MeasurementPoint[],
      currentIsClosed: boolean,
      currentHoverPosition: LngLat | null,
      currentIsHoveringFirstPoint: boolean
    ): void => {
      const startTime = performance.now();
      debugCountersRef.current.syncMeasurementSources += 1;

      if (currentPoints.length === 0) {
        clearRenderedMeasurement(currentMap);
        debugMeasurementTiming('syncMeasurementSources (empty)', startTime);
        return;
      }
      if (
        (!infrastructureReadyRef.current || !hasMeasurementInfrastructure(currentMap)) &&
        !ensureMeasurementInfrastructure(currentMap)
      ) {
        return;
      }

      const vertexCoordinates = currentPoints.map((point) => [point.lngLat.lng, point.lngLat.lat]);
      const lineCoordinates = currentIsClosed
        ? [...vertexCoordinates, vertexCoordinates[0]]
        : vertexCoordinates;

      const lineSource = currentMap.getSource(SOURCE_LINE_ID) as maplibregl.GeoJSONSource | undefined;
      debugCountersRef.current.setDataLine += 1;
      lineSource?.setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: lineCoordinates,
        },
      });

      const hoverLineSource = currentMap.getSource(SOURCE_HOVER_LINE_ID) as
        | maplibregl.GeoJSONSource
        | undefined;
      if (
        currentIsHoveringFirstPoint &&
        currentHoverPosition &&
        currentPoints.length >= 3 &&
        !currentIsClosed
      ) {
        const firstPoint = currentPoints[0];
        const lastPoint = currentPoints[currentPoints.length - 1];
        debugCountersRef.current.setDataHover += 1;
        hoverLineSource?.setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [
              [lastPoint.lngLat.lng, lastPoint.lngLat.lat],
              [firstPoint.lngLat.lng, firstPoint.lngLat.lat],
            ],
          },
        });
      } else {
        debugCountersRef.current.setDataHover += 1;
        hoverLineSource?.setData(EMPTY_LINE_FEATURE);
      }

      const polygonSource = currentMap.getSource(SOURCE_POLYGON_ID) as maplibregl.GeoJSONSource | undefined;
      if (currentIsClosed && currentPoints.length >= 3) {
        debugCountersRef.current.setDataPolygon += 1;
        polygonSource?.setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [[...vertexCoordinates, vertexCoordinates[0]]],
          },
        });
      } else {
        debugCountersRef.current.setDataPolygon += 1;
        polygonSource?.setData(EMPTY_POLYGON_FEATURE);
      }

      const pointsSource = currentMap.getSource(SOURCE_POINTS_ID) as maplibregl.GeoJSONSource | undefined;
      debugCountersRef.current.setDataPoints += 1;
      pointsSource?.setData({
        type: 'FeatureCollection',
        features: currentPoints.map((point, index) => ({
          type: 'Feature',
          properties: {
            id: point.id,
            isFirst: index === 0,
          },
          geometry: {
            type: 'Point',
            coordinates: [point.lngLat.lng, point.lngLat.lat],
          },
        })),
      });

      const segmentPairs = currentPoints
        .slice(0, -1)
        .map((point, index) => [point, currentPoints[index + 1]] as const);
      if (currentIsClosed && currentPoints.length >= 3) {
        segmentPairs.push([currentPoints[currentPoints.length - 1], currentPoints[0]] as const);
      }

      const labelFeatures: Feature<Point, { distance: string }>[] = segmentPairs.map(
        ([startPoint, endPoint]) => {
          const segmentLine = turf.lineString([
            [startPoint.lngLat.lng, startPoint.lngLat.lat],
            [endPoint.lngLat.lng, endPoint.lngLat.lat],
          ]);
          const segmentDistance = turf.length(segmentLine, { units: 'meters' });
          const midpoint = turf.midpoint(
            turf.point([startPoint.lngLat.lng, startPoint.lngLat.lat]),
            turf.point([endPoint.lngLat.lng, endPoint.lngLat.lat])
          );

          return {
            type: 'Feature' as const,
            properties: {
              distance: formatDistanceValue(segmentDistance),
            },
            geometry: midpoint.geometry,
          };
        }
      );

      const labelsSource = currentMap.getSource(SOURCE_LABELS_ID) as maplibregl.GeoJSONSource | undefined;
      debugCountersRef.current.setDataLabels += 1;
      labelsSource?.setData({
        type: 'FeatureCollection',
        features: labelFeatures,
      });

      debugMeasurementTiming('syncMeasurementSources', startTime);
    },
    [clearRenderedMeasurement, ensureMeasurementInfrastructure, hasMeasurementInfrastructure]
  );

  const clearMeasurement = useCallback((): void => {
    pointsRef.current = [];
    isClosedRef.current = false;
    hoverPositionRef.current = null;
    clearRenderedMeasurement(map);

    setPoints([]);
    setDistance(0);
    setArea(0);
    setIsClosed(false);
    setIsHoveringFirstPoint(false);
    isHoveringFirstPointRef.current = false;
    map?.getCanvas().style.setProperty('cursor', 'crosshair');
  }, [clearRenderedMeasurement, map]);

  const removeLastVertex = useCallback((): void => {
    hoverPositionRef.current = null;
    setIsHoveringFirstPoint(false);
    isHoveringFirstPointRef.current = false;

    if (isClosedRef.current) {
      isClosedRef.current = false;
      syncMeasurementSources(map, pointsRef.current, false, null, false);
      setIsClosed(false);
      setDistance(calculateDistance(pointsRef.current));
      setArea(0);
      return;
    }

    const nextPoints = pointsRef.current.slice(0, -1);
    pointsRef.current = nextPoints;
    syncMeasurementSources(map, nextPoints, false, null, false);
    setPoints(nextPoints);
    setDistance(calculateDistance(nextPoints));
    setArea(0);
  }, [calculateDistance, map, syncMeasurementSources]);

  useEffect(() => {
    if (points.length >= 2) {
      setDistance(calculateDistance(points));
    } else {
      setDistance(0);
    }

    if (isClosed && points.length >= 3) {
      setArea(calculateArea(points));
    } else {
      setArea(0);
    }
  }, [points, isClosed, calculateArea, calculateDistance]);

  useEffect(() => {
    if (!isActive || points.length === 0) {
      setIsHoveringFirstPoint(false);
      return;
    }

    if (isClosed) {
      setIsHoveringFirstPoint(false);
      return;
    }

    if (hoverPositionRef.current) {
      setIsHoveringFirstPoint(checkNearFirstPoint(hoverPositionRef.current, points));
    }
  }, [checkNearFirstPoint, isActive, isClosed, points]);

  useEffect(() => {
    if (!map || !isActive) return;

    const handleMouseMove = (event: MapMouseEvent): void => {
      const startTime = performance.now();
      debugCountersRef.current.handleMouseMove += 1;
      hoverPositionRef.current = event.lngLat;
      const currentPoints = pointsRef.current;
      const nearFirst =
        !isClosedRef.current && checkNearFirstPoint(event.lngLat, currentPoints);

      if (nearFirst) {
        map.getCanvas().style.cursor = 'pointer';
      } else {
        map.getCanvas().style.cursor = 'crosshair';
      }

      if (nearFirst !== isHoveringFirstPointRef.current) {
        isHoveringFirstPointRef.current = nearFirst;
        setIsHoveringFirstPoint(nearFirst);
      }

      if (currentPoints.length > 0) {
        syncHoverPreview(map, currentPoints, isClosedRef.current, event.lngLat, nearFirst);
      }

      debugMeasurementTiming('handleMouseMove', startTime);
    };

    map.on('mousemove', handleMouseMove);
    debugMeasurementEvent('listener registered', { type: 'mousemove' });

    return (): void => {
      map.off('mousemove', handleMouseMove);
      hoverPositionRef.current = null;
      debugMeasurementEvent('listener removed', { type: 'mousemove' });
    };
  }, [checkNearFirstPoint, isActive, map, syncHoverPreview]);

  useEffect(() => {
    if (!map || !isActive) return;

    const handleMapClick = (event: MapMouseEvent): void => {
      const startTime = performance.now();
      debugCountersRef.current.handleMapClick += 1;

      if (isClosedRef.current) {
        debugMeasurementTiming('handleMapClick (ignored - already closed)', startTime);
        return;
      }

      const currentPoints = pointsRef.current;
      if (checkNearFirstPoint(event.lngLat, currentPoints)) {
        isClosedRef.current = true;
        syncMeasurementSources(map, currentPoints, true, null, false);
        setIsClosed(true);
        setIsHoveringFirstPoint(false);
        isHoveringFirstPointRef.current = false;
        setDistance(calculateDistance(currentPoints));
        setArea(calculateArea(currentPoints));
        debugMeasurementTiming('handleMapClick (close polygon)', startTime);
        return;
      }

      const newPoint: MeasurementPoint = {
        id: `point-${Date.now()}`,
        lngLat: event.lngLat,
      };

      const nextPoints = [...currentPoints, newPoint];
      pointsRef.current = nextPoints;
      syncMeasurementSources(map, nextPoints, false, null, false);
      setPoints(nextPoints);
      setDistance(calculateDistance(nextPoints));
      setArea(0);
      debugMeasurementTiming('handleMapClick', startTime);
    };

    map.on('click', handleMapClick);
    debugMeasurementEvent('listener registered', { type: 'click' });

    return (): void => {
      map.off('click', handleMapClick);
      debugMeasurementEvent('listener removed', { type: 'click' });
    };
  }, [calculateArea, calculateDistance, checkNearFirstPoint, isActive, map, syncMeasurementSources]);

  useEffect(() => {
    if (!isActive) return;

    const startTime = performance.now();

    if (map && ensureMeasurementInfrastructure(map)) {
      clearRenderedMeasurement(map);
      map.getCanvas().style.setProperty('cursor', 'crosshair');
      debugMeasurementTiming('activate -> infrastructure ready', startTime);
    } else if (map && !map.isStyleLoaded()) {
      // Style not yet loaded — styledata handler will create infrastructure when ready.
      if (isMeasurementDebugEnabled()) {
        console.debug('[MeasurementTool] activate: style not loaded, deferring to styledata');
      }
    }
  }, [clearRenderedMeasurement, ensureMeasurementInfrastructure, isActive, map]);

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (isEditableElement(event.target)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        clearMeasurement();
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        if (pointsRef.current.length === 0 && !isClosedRef.current) return;
        event.preventDefault();
        removeLastVertex();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    debugMeasurementEvent('listener registered', { type: 'keydown' });
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown);
      debugMeasurementEvent('listener removed', { type: 'keydown' });
    };
  }, [clearMeasurement, isActive, removeLastVertex]);

  useEffect(() => {
    if (!map || !isActive) return;

    const handleStyleData = (event: { dataType?: string }): void => {
      if (event.dataType !== 'style') return;
      styledataCountRef.current += 1;
      debugCountersRef.current.styledata += 1;
      infrastructureReadyRef.current = false;

      markerImageCreated.current = false;

      if (styleReloadTimerRef.current !== null) {
        window.clearTimeout(styleReloadTimerRef.current);
      }

      styleReloadTimerRef.current = window.setTimeout(() => {
        styleReloadTimerRef.current = null;
        const startTime = performance.now();
        const ready = ensureMeasurementInfrastructure(map);
        if (ready) {
          if (pointsRef.current.length > 0) {
            syncMeasurementSources(
              map,
              pointsRef.current,
              isClosedRef.current,
              hoverPositionRef.current,
              isHoveringFirstPointRef.current
            );
          } else {
            clearRenderedMeasurement(map);
          }
        }
        setStyleRefreshNonce((current) => current + 1);
        debugMeasurementTiming(
          `styledata rehydrate (#${styledataCountRef.current})`,
          startTime
        );
      }, STYLE_REINJECTION_DELAY_MS);
    };

    map.on('styledata', handleStyleData as any);
    debugMeasurementEvent('listener registered', { type: 'styledata' });

    return (): void => {
      map.off('styledata', handleStyleData as any);
      debugMeasurementEvent('listener removed', { type: 'styledata' });
      if (styleReloadTimerRef.current !== null) {
        window.clearTimeout(styleReloadTimerRef.current);
        styleReloadTimerRef.current = null;
      }
    };
  }, [
    clearRenderedMeasurement,
    ensureMeasurementInfrastructure,
    isActive,
    map,
    syncMeasurementSources,
  ]);

  useEffect(() => {
    if (!map || points.length > 0) return;
    clearRenderedMeasurement(map);
  }, [clearRenderedMeasurement, map, points.length]);

  useEffect(() => {
    if (isActive || !map) return;

    debugMeasurementEvent('session counters', { ...debugCountersRef.current });
    infrastructureReadyRef.current = false;
    removeMeasurementArtifacts(map);
    map.getCanvas().style.cursor = '';
    markerImageCreated.current = false;
    clearMeasurement();
  }, [clearMeasurement, isActive, map]);

  useEffect(() => {
    return (): void => {
      if (copyFeedbackTimerRef.current !== null) {
        window.clearTimeout(copyFeedbackTimerRef.current);
      }
      debugMeasurementEvent('session counters', { ...debugCountersRef.current });
      if (styleReloadTimerRef.current !== null) {
        window.clearTimeout(styleReloadTimerRef.current);
      }
      if (map) {
        removeMeasurementArtifacts(map);
        map.getCanvas().style.cursor = '';
      }
    };
  }, [map]);

  const sendDistanceToCalculator = (): void => {
    onSendToCalculator?.(distance);
  };

  const sendAreaToCalculator = (): void => {
    onSendToCalculator?.(area);
  };

  const copyToClipboard = useCallback(async (
    value: string,
    field: 'distance' | 'area'
  ): Promise<void> => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedField(field);
      if (copyFeedbackTimerRef.current !== null) {
        window.clearTimeout(copyFeedbackTimerRef.current);
      }
      copyFeedbackTimerRef.current = window.setTimeout(() => {
        setCopiedField(null);
        copyFeedbackTimerRef.current = null;
      }, 1400);
    } catch (error) {
      if (isMeasurementDebugEnabled()) {
        console.warn('[MeasurementTool] copyToClipboard failed', error);
      }
    }
  }, []);

  const vertexCount = points.length;

  if (!isActive) return null;

  return (
    <div className="measurement-panel">
      <div className="measurement-header">
        <div className="measurement-title-wrapper">
          <Ruler size={16} />
          <h3 className="measurement-title">Medición</h3>
        </div>
        <button className="measurement-close" onClick={onDeactivate} aria-label="Cerrar medición">
          <X size={16} />
        </button>
      </div>

      <div className="measurement-content">
        {vertexCount === 0 && (
          <p className="measurement-hint">Haz clic en el mapa para comenzar a medir</p>
        )}

        {vertexCount > 0 && (
          <>
            <div className="measurement-info">
              <span className="measurement-label">Vértices:</span>
              <span className="measurement-value">{vertexCount}</span>
            </div>

            {vertexCount >= 2 && (
              <div className="measurement-result">
                <div className="measurement-result-row">
                  <span className="measurement-label">Distancia:</span>
                  <span className="measurement-value">{formatDistanceValue(distance)}</span>
                </div>
                <div className="measurement-result-actions">
                  <button
                    className={`measurement-copy-btn ${copiedField === 'distance' ? 'is-copied' : ''}`}
                    onClick={() => copyToClipboard(formatDistanceValue(distance), 'distance')}
                    title={copiedField === 'distance' ? 'Copiado' : 'Copiar valor'}
                    aria-label="Copiar distancia"
                  >
                    {copiedField === 'distance' ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                  <button
                    className="measurement-send-btn"
                    onClick={sendDistanceToCalculator}
                    title="Enviar a calculadora"
                    aria-label="Enviar distancia a calculadora"
                  >
                    <Calculator size={14} />
                  </button>
                </div>
              </div>
            )}

            {isClosed && area > 0 && (
              <div className="measurement-result">
                <div className="measurement-result-row">
                  <span className="measurement-label">Área:</span>
                  <span className="measurement-value">{formatAreaValue(area)}</span>
                </div>
                <div className="measurement-result-actions">
                  <button
                    className={`measurement-copy-btn ${copiedField === 'area' ? 'is-copied' : ''}`}
                    onClick={() => copyToClipboard(formatAreaValue(area), 'area')}
                    title={copiedField === 'area' ? 'Copiado' : 'Copiar valor'}
                    aria-label="Copiar área"
                  >
                    {copiedField === 'area' ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                  <button
                    className="measurement-send-btn"
                    onClick={sendAreaToCalculator}
                    title="Enviar a calculadora"
                    aria-label="Enviar área a calculadora"
                  >
                    <Calculator size={14} />
                  </button>
                </div>
              </div>
            )}

            <button className="measurement-clear-btn" onClick={clearMeasurement}>
              <Trash2 size={14} />
              <span>Limpiar</span>
            </button>
          </>
        )}
      </div>

      <div className="measurement-footer">
        <p className="measurement-info-text">
          {isClosed
            ? 'Polígono cerrado. Backspace reabre el trazado; Escape limpia la medición.'
            : isHoveringFirstPoint && vertexCount >= 3
            ? `Haz clic para cerrar el polígono (${CLOSE_THRESHOLD_PX}px de tolerancia).`
            : vertexCount >= 2
            ? 'Sigue agregando vértices. Backspace deshace; Escape limpia.'
            : 'Haz clic para agregar vértices. Escape limpia la medición.'}
        </p>
      </div>
    </div>
  );
}
